import React, { useEffect, useState } from "react";
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Link,
    Stack,
    Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PlayCircleOutlineIcon from "@mui/icons-material/PlayCircleOutline";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import releaseNoteServices from "app/services/release-note-services";

// Novedades para quien usa Haulers, en la web. Son las mismas tandas que muestra la app, y
// cada una puede traer un video y un PDF que la explican. Publicarlas es de superadmin, en
// "Publicar novedades" (ReleaseNotesIndex).
//
// Aca no se filtra por version: ese filtro es de la app, que no puede anunciar un cambio que
// el telefono todavia no recibio. La web siempre corre lo ultimo.

const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto",
    "septiembre", "octubre", "noviembre", "diciembre"];

// "2026-10-02" -> "2 de octubre de 2026". A mano y no con Date: "2026-10-02" se lee como UTC
// medianoche y en Chile queda en el dia anterior.
const formatearFecha = (iso) => {
    const [anio, mes, dia] = String(iso || "").split("-").map(Number);
    if (!anio || !mes || !dia) return iso;
    return `${dia} de ${MESES[mes - 1]} de ${anio}`;
};

const NovedadesPage = () => {
    const [notas, setNotas] = useState([]);
    const [cargando, setCargando] = useState(true);
    const [error, setError] = useState(false);
    const [video, setVideo] = useState(null);
    const [abriendoPdf, setAbriendoPdf] = useState(null);
    const [abriendoVideo, setAbriendoVideo] = useState(null);

    useEffect(() => {
        // Solo las de la web (y las de web y app): las "solo app" no se muestran aca.
        releaseNoteServices.getReleaseNotes("?audience=web")
            .then((response) => setNotas(response.data || []))
            .catch(() => setError(true))
            .finally(() => setCargando(false));
    }, []);

    // El video subido a Haulers se reproduce aca, con un link que vence: se pide al abrirlo.
    // Un link de YouTube, Vimeo o Loom va en su reproductor; uno que no se puede embeber, afuera.
    const verVideo = async (nota) => {
        if (nota.video_file_name) {
            setAbriendoVideo(nota.id);
            try {
                const { url } = await releaseNoteServices.getVideoUrl(nota.id);
                setVideo({ ...nota, archivo: url });
            } catch (_error) {
                // El interceptor global ya muestra el mensaje.
            } finally {
                setAbriendoVideo(null);
            }
        } else if (nota.video_embed_url) {
            setVideo(nota);
        } else {
            window.open(nota.video_url, "_blank", "noopener");
        }
    };

    // La pestaña se abre ANTES de pedir el link: abierta después de una llamada, el navegador
    // la toma como ventana emergente y la bloquea.
    const verPdf = async (nota) => {
        const pestana = window.open("", "_blank");
        setAbriendoPdf(nota.id);
        try {
            const { url } = await releaseNoteServices.getDocumentUrl(nota.id);
            if (pestana) pestana.location.href = url;
            else window.location.href = url;
        } catch (_error) {
            if (pestana) pestana.close();
        } finally {
            setAbriendoPdf(null);
        }
    };

    return (
        <Box sx={{ maxWidth: 820 }}>
            <Typography variant="h1" sx={{ mb: 1 }}>Novedades</Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                Lo nuevo en Haulers, de lo más reciente a lo más antiguo.
            </Typography>

            {cargando && (
                <Stack alignItems="center" sx={{ py: 8 }}>
                    <CircularProgress color="warning" />
                </Stack>
            )}

            {!cargando && error && (
                <Typography color="text.secondary">No pudimos cargar las novedades. Intenta de nuevo en un rato.</Typography>
            )}

            {!cargando && !error && notas.length === 0 && (
                <Typography color="text.secondary">Todavía no hay novedades.</Typography>
            )}

            <Stack spacing={2}>
                {notas.map((nota) => (
                    <Card key={nota.id} variant="outlined" sx={{ borderRadius: 3 }}>
                        <CardContent>
                            <Typography variant="h4" sx={{ mb: 1.5 }}>{formatearFecha(nota.released_on)}</Typography>

                            <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
                                {(nota.items || []).map((item) => (
                                    <Typography component="li" variant="body1" key={item} sx={{ mb: 0.75 }}>
                                        {item}
                                    </Typography>
                                ))}
                            </Box>

                            {(nota.video_url || nota.video_file_name || nota.document_name) && (
                                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }} flexWrap="wrap">
                                    {(nota.video_url || nota.video_file_name) && (
                                        <Button
                                            variant="contained"
                                            size="small"
                                            startIcon={abriendoVideo === nota.id
                                                ? <CircularProgress size={14} color="inherit" />
                                                : <PlayCircleOutlineIcon />}
                                            disabled={abriendoVideo === nota.id}
                                            onClick={() => verVideo(nota)}
                                        >
                                            Ver video
                                        </Button>
                                    )}
                                    {nota.document_name && (
                                        <Button
                                            variant="outlined"
                                            size="small"
                                            startIcon={abriendoPdf === nota.id
                                                ? <CircularProgress size={14} />
                                                : <PictureAsPdfOutlinedIcon />}
                                            disabled={abriendoPdf === nota.id}
                                            onClick={() => verPdf(nota)}
                                        >
                                            Ver PDF
                                        </Button>
                                    )}
                                </Stack>
                            )}
                        </CardContent>
                    </Card>
                ))}
            </Stack>

            {/* El video subido manda en el tamano: uno horizontal (el de las novedades, grabado de la
                pantalla) llega hasta "lg" y uno vertical (grabado con el celular) queda limitado por
                el alto, asi el dialogo no queda chico para el primero ni gigante para el segundo. */}
            <Dialog fullWidth={!video?.archivo} maxWidth={video?.archivo ? "lg" : "md"} open={Boolean(video)} onClose={() => setVideo(null)}>
                <DialogTitle sx={{ pr: 6 }}>
                    {video && formatearFecha(video.released_on)}
                    <IconButton
                        aria-label="Cerrar"
                        onClick={() => setVideo(null)}
                        sx={{ position: "absolute", right: 8, top: 8 }}
                    >
                        <CloseIcon />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    {video?.archivo && (
                        <Box
                            component="video"
                            src={video.archivo}
                            controls
                            autoPlay
                            playsInline
                            sx={{ display: "block", mx: "auto", width: "auto", maxWidth: "100%", maxHeight: "80vh", borderRadius: 2, bgcolor: "#000" }}
                        />
                    )}
                    {video && !video.archivo && (
                        <>
                            <Box sx={{ position: "relative", pt: "56.25%", borderRadius: 2, overflow: "hidden", bgcolor: "#000" }}>
                                <Box
                                    component="iframe"
                                    src={video.video_embed_url}
                                    title="Video de la novedad"
                                    allow="autoplay; encrypted-media; picture-in-picture; fullscreen"
                                    allowFullScreen
                                    sx={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: 0 }}
                                />
                            </Box>
                            <Link href={video.video_url} target="_blank" rel="noopener" variant="body2" sx={{ display: "inline-block", mt: 1.5 }}>
                                Abrir en otra pestaña
                            </Link>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default NovedadesPage;
